/**
 * introAssembly — build a presentation intro from separate clips at run time
 * (Tom 2026-10-11).
 *
 *   [The Spanish for] [LEGO prompt] [is]  [, as in] [context — only if scored ≥ 3]
 *
 * WHY ONE CLIP, NOT A SEQUENCE. The engine's prompt phase is one audio
 * element, one 'ended' event, one watchdog. Chaining four clips through it
 * would put a gap and a stall risk at every join on iOS/Android/offline. So
 * every piece is fetched and decoded FIRST (nothing sounds until all are
 * ready), joined with the gap below into one 16-bit WAV blob (WebKit plays WAV
 * blobs, refuses mp3 ones — see cache/wav.ts), and handed to the engine as an
 * ordinary prompt URL. If anything is missing or fails, the planner/assembler
 * returns null and the engine plays the existing single presentation clip.
 */
import { bytesToPcm, encodePcmWav, type PcmClip } from '../cache/wav'
export { voicedRms, limitPeaks, toDb } from '../cache/loudness'
import {
  CLIP_TARGET_DB, MAX_GAIN, normaliseLevel, scaleClip, voicedRms,
} from '../cache/loudness'
import {
  INTRO_BANK, baseLanguageOfCourse, pickVariant, type IntroBank,
} from './introBank'

/** Default silence between pieces. THE one tunable — see getIntroPieceGapMs. */
export const DEFAULT_INTRO_PIECE_GAP_MS = 120

/** The "as in" + context pieces play only at or above this score. */
export const INTRO_CONTEXT_MIN_SCORE = 3

const GAP_MIN_MS = 0
const GAP_MAX_MS = 1500

/**
 * The inter-piece pause — ONE setting. Override for judging by ear, in this
 * order: `?introGap=<ms>` on the page URL, then localStorage `ssi_intro_gap_ms`,
 * then the default above.
 */
export function getIntroPieceGapMs(): number {
  const clamp = (n: number) => Math.min(GAP_MAX_MS, Math.max(GAP_MIN_MS, Math.round(n)))
  try {
    const q = new URLSearchParams(globalThis.location?.search ?? '').get('introGap')
    if (q !== null && q !== '' && Number.isFinite(Number(q))) return clamp(Number(q))
    const stored = globalThis.localStorage?.getItem('ssi_intro_gap_ms')
    if (stored !== null && stored !== undefined && stored !== '' && Number.isFinite(Number(stored))) {
      return clamp(Number(stored))
    }
  } catch { /* storage / location unavailable — default */ }
  return DEFAULT_INTRO_PIECE_GAP_MS
}

export interface IntroPlanInput {
  courseCode: string | null | undefined
  /** Voice id of the course's known voice (voice_config.voices.known.voiceId). */
  knownVoiceId: string | null | undefined
  legoId: string
  /** The LEGO's own known-language clip (Charlotte), already a playable URL. */
  legoPromptUrl: string | null | undefined
  /** Builds a playable URL from a bank clip id. */
  audioUrl: (audioId: string) => string
  /** Optional "as in" context clip; used only when scoreIsKnown ≥ 3. Missing = no context. */
  context?: { audioUrl: string; score: number } | null
  /** Audition override: ignore the known-voice match. */
  ignoreVoiceMatch?: boolean
  bank?: IntroBank
}

/** Ordered piece URLs, or null when this intro should play its single clip. */
export function planIntroPieces(input: IntroPlanInput): string[] | null {
  const bank = input.bank ?? INTRO_BANK
  const base = baseLanguageOfCourse(input.courseCode)
  if (!base || !input.legoPromptUrl) return null
  // The pieces are Charlotte; the LEGO prompt sits between them, so it must be
  // her too or the learner hears three voices in one sentence.
  if (!input.ignoreVoiceMatch && !(input.knownVoiceId && input.knownVoiceId.includes(bank.voiceId))) return null

  const langFor = pickVariant(bank.langFor[base], `${input.legoId}|langFor`)
  const is = pickVariant(bank.is, `${input.legoId}|is`)
  if (!langFor || !is) return null

  const pieces = [input.audioUrl(langFor.id), input.legoPromptUrl, input.audioUrl(is.id)]
  const ctx = input.context
  if (ctx && ctx.audioUrl && ctx.score >= INTRO_CONTEXT_MIN_SCORE) {
    const asIn = pickVariant(bank.asIn, `${input.legoId}|asIn`)
    if (asIn) pieces.push(input.audioUrl(asIn.id), ctx.audioUrl)
  }
  return pieces
}

/**
 * How far the LEGO prompt sits ABOVE the frame pieces ("The Spanish for", "is",
 * "as in" …), in dB. THE emphasis knob (Tom 2026-10-12: the intro pauses to
 * emphasise the LEGO, so it must be louder). +6 was too much once the source
 * clips' own level variation was audible; +3 (Tom 03:21Z) is the safer lift.
 */
export const INTRO_LEGO_EMPHASIS_DB = 3

/**
 * Where every assembled intro lands overall (voiced RMS, dBFS). Measured
 * 2026-10-12 over the first 30 spa_for_eng LEGOs: the target-language clips the
 * learner hears next sit at about -14 dBFS, so an intro at the same level never
 * jumps up or down against them.
 */
export const INTRO_TARGET_DB = CLIP_TARGET_DB

/** Index of the LEGO prompt in the planned piece list. */
export const INTRO_LEGO_INDEX = 1

/**
 * Per-piece linear gains: frame pieces to one fixed level, the LEGO piece
 * (`legoIndex`) `emphasisDb` above it. The level is ABSOLUTE (not "relative to
 * the loudest piece"), so every intro is built the same way whatever its clips
 * were recorded at; peaks are NOT handled here — the limiter does that on the
 * joined intro without disturbing the dB relationship. Pure.
 */
export function matchLoudnessGains(
  clips: PcmClip[],
  emphasisDb: number = INTRO_LEGO_EMPHASIS_DB,
  legoIndex: number = INTRO_LEGO_INDEX,
): number[] {
  const levels = clips.map(voicedRms)
  const frameTarget = 10 ** ((INTRO_TARGET_DB - emphasisDb * 0.5) / 20)
  const lift = 10 ** (emphasisDb / 20)
  return clips.map((c, i) => {
    if (levels[i] <= 0) return 1
    return Math.min(MAX_GAIN, (i === legoIndex ? frameTarget * lift : frameTarget) / levels[i])
  })
}

/** Apply `matchLoudnessGains` and return new clips (inputs untouched). */
export function matchLoudness(
  clips: PcmClip[],
  emphasisDb: number = INTRO_LEGO_EMPHASIS_DB,
  legoIndex: number = INTRO_LEGO_INDEX,
): PcmClip[] {
  const gains = matchLoudnessGains(clips, emphasisDb, legoIndex)
  return clips.map((c, i) => scaleClip(c, gains[i]))
}

/**
 * Bring the WHOLE joined intro to `targetDb` (voiced RMS) and keep it under the
 * peak ceiling — the shared play-time levelling (cache/loudness.ts). A global
 * gain leaves the LEGO-over-frame relationship exactly as shaped.
 */
export function normaliseIntro(clip: PcmClip, targetDb: number = INTRO_TARGET_DB): PcmClip {
  return normaliseLevel(clip, targetDb)
}

/** Tom-006 intro ending (rulings r-2026-10-12-tom-006-intro-clips-get-an): 80 ms equal-power fade over the last speech, then 250 ms of silence. */
export const TOM_FADE_MS = 80
export const TOM_TAIL_MS = 250

/** Pure. End of speech = the last 5 ms frame above -45 dB; the fade ends there and everything after it becomes the silent tail. */
export function applyTomEnding(clip: PcmClip): PcmClip {
  const sr = clip.sampleRate
  const frame = Math.max(1, Math.round(sr * 0.005))
  const n = clip.channels[0].length
  let eos = -1
  for (let i = 0; i + frame <= n; i += frame) {
    let e = 0
    for (const ch of clip.channels) for (let j = i; j < i + frame; j++) e += ch[j] * ch[j]
    if (20 * Math.log10(Math.sqrt(e / (frame * clip.channels.length)) + 1e-12) > -45) eos = i + frame
  }
  if (eos < 0) return clip
  const fade = Math.min(eos, Math.round((sr * TOM_FADE_MS) / 1000))
  const total = eos + Math.round((sr * TOM_TAIL_MS) / 1000)
  return {
    sampleRate: sr,
    channels: clip.channels.map((ch) => {
      const o = new Float32Array(total)
      o.set(ch.subarray(0, eos))
      for (let i = 0; i < fade; i++) o[eos - fade + i] *= Math.cos((i / fade) * (Math.PI / 2))
      return o
    }),
  }
}

/** Join decoded clips with `gapMs` of silence between them. Pure. */
export function concatWithGaps(clips: PcmClip[], gapMs: number): PcmClip {
  const sampleRate = clips[0].sampleRate
  const numChannels = Math.max(...clips.map((c) => c.channels.length))
  const gapSamples = Math.round((sampleRate * gapMs) / 1000)
  const total = clips.reduce((n, c) => n + c.channels[0].length, 0) + gapSamples * (clips.length - 1)
  const channels = Array.from({ length: numChannels }, () => new Float32Array(total))
  let at = 0
  clips.forEach((clip, i) => {
    for (let ch = 0; ch < numChannels; ch++) {
      // A mono clip among stereo ones plays on both channels.
      channels[ch].set(clip.channels[Math.min(ch, clip.channels.length - 1)], at)
    }
    at += clip.channels[0].length + (i < clips.length - 1 ? gapSamples : 0)
  })
  return { sampleRate, channels }
}

export interface AssembleDeps {
  /** Turns a piece URL into one the device can read now (offline cache aware). */
  resolveUrl?: (url: string) => Promise<string>
  fetchBytes?: (url: string) => Promise<ArrayBuffer>
  decode?: (bytes: ArrayBuffer) => Promise<PcmClip | null>
  makeBlobUrl?: (blob: Blob) => string
  /** Tom-006 pieces: finish with the Tom intro ending (fade + silent tail). */
  tomEnding?: boolean
}

const defaultFetchBytes = async (url: string): Promise<ArrayBuffer> => {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`)
  return res.arrayBuffer()
}

/**
 * Fetch + decode EVERY piece, then splice. Null on any failure, so a missing
 * piece never yields a half-sentence — the caller plays the single clip.
 */
export async function assembleIntroClip(
  urls: string[],
  gapMs: number,
  deps: AssembleDeps = {},
): Promise<string | null> {
  const fetchBytes = deps.fetchBytes ?? defaultFetchBytes
  const decode = deps.decode ?? bytesToPcm
  const makeBlobUrl = deps.makeBlobUrl ?? ((b: Blob) => URL.createObjectURL(b))
  try {
    const clips = await Promise.all(urls.map(async (u) => {
      const readable = deps.resolveUrl ? await deps.resolveUrl(u) : u
      const pcm = await decode(await fetchBytes(readable))
      if (!pcm || pcm.channels.length === 0 || pcm.channels[0].length === 0) {
        throw new Error(`piece did not decode: ${u}`)
      }
      return pcm
    }))
    if (new Set(clips.map((c) => c.sampleRate)).size !== 1) {
      throw new Error('pieces decoded at different sample rates')
    }
    const joined = normaliseIntro(concatWithGaps(matchLoudness(clips), gapMs))
    return makeBlobUrl(encodePcmWav(deps.tomEnding ? applyTomEnding(joined) : joined))
  } catch (err) {
    console.warn('[introAssembly] falling back to the single presentation clip:', (err as Error)?.message)
    return null
  }
}

const MEMO_CAP = 48
const memo = new Map<string, Promise<string | null>>()

/**
 * Memoised assembly: the pre-warm one cycle ahead and the play-time call share
 * one promise, so the pieces are fetched once. Failures are not kept — a later
 * attempt (back online) may succeed.
 */
export function assembleIntroMemoised(
  urls: string[],
  gapMs: number,
  deps: AssembleDeps = {},
): Promise<string | null> {
  const key = `${gapMs}|${deps.tomEnding ?? false}|${urls.join('|')}`
  const hit = memo.get(key)
  if (hit) return hit
  const p = assembleIntroClip(urls, gapMs, deps).then((url) => {
    if (!url) memo.delete(key)
    return url
  })
  memo.set(key, p)
  if (memo.size > MEMO_CAP) {
    const oldest = memo.keys().next().value as string
    const old = memo.get(oldest)
    memo.delete(oldest)
    void old?.then((u) => { if (u?.startsWith('blob:')) { try { URL.revokeObjectURL(u) } catch { /* ignore */ } } })
  }
  return p
}

export function _resetIntroAssemblyMemoForTests(): void {
  memo.clear()
}
